import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Code, Brain, Cpu, ArrowRight } from 'lucide-react';

const CarnegieMellon: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Carnegie Mellon University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#C41230] selection:bg-[#C41230] selection:text-white">
             {/* Header - CMU Red */}
             <div className="bg-[#C41230] text-white py-1 px-4"></div>
             
             {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-100">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="font-sans font-bold text-3xl md:text-4xl text-[#C41230] tracking-tighter">
                        {schoolName.split(' ').slice(0, -1).join(' ')} <br className="hidden md:block"/> 
                        <span className="md:text-2xl font-light text-gray-800 tracking-tight">{schoolName.split(' ').pop()}</span>
                    </div>

                    <div className="hidden lg:flex gap-10 font-bold text-gray-800 text-sm">
                        <a href="#" className="hover:text-[#C41230] transition-colors relative group">
                            About
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#C41230] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#C41230] transition-colors relative group">
                            Academics
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#C41230] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#C41230] transition-colors relative group">
                            Admission
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#C41230] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#C41230] transition-colors relative group">
                            Research
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#C41230] transition-all group-hover:w-full"></span>
                        </a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <div className="hidden md:flex flex-col items-end text-[10px] font-bold uppercase text-gray-500 tracking-widest mr-4">
                             <span>Pittsburgh, PA</span>
                             <span>Founded 1900</span>
                         </div>
                        <Search className="text-gray-800 cursor-pointer hover:text-[#C41230]" size={22} />
                        <Menu className="text-gray-800 cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Interdisciplinary */}
            <header className="relative h-[650px] bg-gray-100">
                 <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                     <div className="p-12 md:p-20 flex flex-col justify-center relative z-10">
                         <h1 className="text-5xl md:text-7xl font-bold text-[#333] mb-8 leading-tight">
                             Work that matters.
                         </h1>
                         <p className="text-xl text-gray-600 mb-12 max-w-md leading-relaxed">
                             At {schoolName.split(' ')[0]}, we define the future by turning imagination into reality through technology, arts, and science.
                         </p>
                         <Button className="bg-[#C41230] hover:bg-[#a00e26] text-white font-bold text-lg px-8 h-14 w-fit rounded-none">
                             View Our Programs
                         </Button>
                     </div>
                     <div className="relative h-full overflow-hidden">
                          {/* Tartan-ish overlay effect */}
                          <div className="absolute inset-0 bg-[#C41230]/10 z-10 mix-blend-multiply"></div>
                          <img 
                            src="https://images.unsplash.com/photo-1517048676732-d65fa9374075?q=80&w=2070&auto=format&fit=crop" 
                            alt="CMU Robotics" 
                            className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                        />
                     </div>
                 </div>
            </header>

            {/* Stats - Tech Vibe */}
            <section className="bg-[#222] text-white py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 text-center">
                     <div>
                         <Brain size={48} className="text-[#C41230] mx-auto mb-6" />
                         <div className="text-4xl font-bold mb-2">#1</div>
                         <p className="text-gray-400 font-bold uppercase text-sm tracking-widest">In Artificial Intelligence</p>
                     </div>
                     <div>
                         <Cpu size={48} className="text-[#C41230] mx-auto mb-6" />
                         <div className="text-4xl font-bold mb-2">#1</div>
                         <p className="text-gray-400 font-bold uppercase text-sm tracking-widest">In Computer Science</p>
                     </div>
                     <div>
                         <Code size={48} className="text-[#C41230] mx-auto mb-6" />
                         <div className="text-4xl font-bold mb-2">#1</div>
                         <p className="text-gray-400 font-bold uppercase text-sm tracking-widest">In Computer Engineering</p>
                     </div>
                </div>
            </section>

             {/* News List - Clean */}
             <section className="py-24 px-6 md:px-12 max-w-[1000px] mx-auto">
                 <h2 className="text-3xl font-bold text-[#333] mb-12 border-l-8 border-[#C41230] pl-6">Latest from {schoolName.split(' ')[0]}</h2>

                 <div className="space-y-12">
                     {[
                         { date: "October 24", title: "Robotics Institute develops new autonomous search and rescue drone", tag: "Technology" },
                         { date: "October 21", title: "Drama students premiere new play exploring digital ethics", tag: "Arts" },
                         { date: "October 18", title: "Tepper School launches new center for sustainable business", tag: "Business" },
                     ].map((news, i) => (
                         <div key={i} className="group cursor-pointer flex flex-col md:flex-row gap-6 items-baseline border-b border-gray-100 pb-8 last:border-0">
                             <div className="w-32 text-gray-400 font-bold text-sm uppercase tracking-widest flex-shrink-0">{news.date}</div>
                             <div>
                                 <span className="text-[#C41230] text-xs font-bold uppercase tracking-widest mb-2 block">{news.tag}</span>
                                 <h3 className="text-2xl font-bold text-[#333] group-hover:text-[#C41230] transition-colors mb-2 leading-tight">{news.title}</h3>
                                 <div className="flex items-center gap-2 text-sm text-gray-500 font-bold mt-2 group-hover:ml-2 transition-all">
                                     Read Story <ArrowRight size={14} />
                                 </div>
                             </div>
                         </div>
                     ))}
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#f2f2f2] text-[#333] py-20 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                      <div className="max-w-xs">
                          <h4 className="font-bold text-2xl mb-6">{schoolName}</h4>
                          <p className="text-sm text-gray-600 mb-4">
                              5000 Forbes Avenue <br/>
                              Pittsburgh, PA 15213 <br/>
                              412-268-2000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 gap-12 text-sm font-bold text-gray-600">
                          <ul className="space-y-3">
                              <li><a href="#" className="hover:text-[#C41230]">Map</a></li>
                              <li><a href="#" className="hover:text-[#C41230]">Directory</a></li>
                              <li><a href="#" className="hover:text-[#C41230]">A-Z Index</a></li>
                          </ul>
                          <ul className="space-y-3">
                               <li><a href="#" className="hover:text-[#C41230]">Consumer Information</a></li>
                              <li><a href="#" className="hover:text-[#C41230]">Privacy Policy</a></li>
                              <li><a href="#" className="hover:text-[#C41230]">Accessibility</a></li>
                          </ul>
                      </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-400">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default CarnegieMellon;
